import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from './components/layout/Dashboardlayout';
import MetricCard from './components/dashboard/MetricCard';
import BusinessSetup from './components/business/BusinessSetup';
import { Eye, Users, Star, FileText, TrendingUp, Sparkles, Loader2 } from 'lucide-react';
//import { useContent } from './hooks/useContent';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

import './Dashboard.css';

const Dashboard2 = () => {
    
  const navigate = useNavigate();
  const location = useLocation();

  const sessionId = location.state?.sessionId || localStorage.getItem('session_id');

  const [user, setUser] = useState(null);
  const [business, setBusiness] = useState([]);
  const [showSetup, setShowSetup] = useState(false);
  const [loading, setLoading] = useState(true);

  // 🔥 MAIN FETCH (user + business)
  const fetchData = async () => {
    try {
      setLoading(true);

      const ress = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/user`,
          {
            params: { sessionId },
          }
       );
      setUser(ress.data);

      if (!localStorage.getItem('session_id')) {
            localStorage.setItem('session_id', sessionId);
        }

      // ✅ get businesses
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/businesses`, {
            params: { owner_id: ress.data.userId },
          }
        );

      const businesses = res.data;

      // ✅ enrich data
      const enrichedBusinesses = await Promise.all(
        businesses.map(async (biz) => {
          const [leadsRes, reviewsRes, contentRes] = await Promise.all([
            axios.get(`${process.env.REACT_APP_BACKEND_URL}/leads`, {
              params: { user_id: ress.data.userId, business_id: biz.id }
            }),  
            axios.get(`${process.env.REACT_APP_BACKEND_URL}/reviews`, {
              params: { business_id: biz.id }
            }),
            axios.get(`${process.env.REACT_APP_BACKEND_URL}/generated-content`, {
              params: { business_id: biz.id }
            }),
          ]);
          const leads = leadsRes.data || [];
          const reviews = reviewsRes.data || [];
          const contents = contentRes.data || [];

          const totalLeads = leads.length;
          const convertedLeads = leads.filter(l => l.status === 'converted').length;
          const conversionRate = totalLeads
            ? Math.round((convertedLeads / totalLeads) * 100)
            : 0;

          const avgRating = reviews.length
            ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
            : '0.0';

          const positiveReviews = reviews.filter(r => r.sentiment === 'positive').length;

          const totalClicks = contents.reduce((sum, c) => sum + (c.clicks || 0), 0);

          const topContent = [...contents]
            .sort((a, b) => (b.clicks || 0) - (a.clicks || 0))
            .slice(0, 3);

          return {
            ...biz,
            leads,
            reviews,
            contents,
            totalLeads,
            convertedLeads,
            conversionRate,
            avgRating,
            positiveReviews,
            totalClicks,
            topContent,
          };
        })
      );

      setBusiness(enrichedBusinesses);

      // ✅ show setup if no business
      if (enrichedBusinesses.length === 0) {
        setShowSetup(true);
      }

    } catch (err) {
      console.error('Error loading dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  // 🔥 run once
  useEffect(() => {
    if (!sessionId) {
      navigate('/auth');
      return;
    }

    fetchData();
  }, [sessionId]);

  // 🔥 loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  // 🔥 setup screen
  if (business.length === 0) {
    return (
      <BusinessSetup
        userId = {user?.id} 
        onComplete = {() => {
          setShowSetup(false);
          fetchData();
        }}
      />
    );
  }

  return (
  <div style={{  display: 'flex', overflowY: 'auto'}}>
  <div className="scroll-wrapper">
    <DashboardLayout>
      <div className="dashboard-container"> 
        <button className="btn secondary" onClick={() => navigate(`/business/setup`)}>
                    <Star size={20} /> Add new business
        </button>

        {business.map((biz) => {
          const leads = biz.leads || [];
          const reviews = biz.reviews || [];
          const contents = biz.contents || [];

          const totalLeads = leads.length;
          const convertedLeads = leads.filter(l => l.status === 'converted').length;
          const conversionRate = totalLeads
            ? Math.round((convertedLeads / totalLeads) * 100)
            : 0;

          const avgRating = reviews.length
            ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
            : '0.0';

          const positiveReviews = reviews.filter(r => r.sentiment === 'positive').length;

          const totalClicks = contents.reduce((sum, c) => sum + (c.clicks || 0), 0);

          const topContent = [...contents]
            .sort((a, b) => (b.clicks || 0) - (a.clicks || 0))
            .slice(0, 3);

          return (
            <div key={biz.id} style={{ marginBottom: '4rem' }}>

              {/* Header */}
              <div className="dashboard-header">
                <h1>{biz.name}</h1>
                <p>{biz.industry} • Dashboard Overview</p>
              </div>

              {/* Metrics */}
              <div className="metrics-grid">
                <MetricCard title="Total Clicks" value={totalClicks} subtitle="From all content" icon={Eye} variant="primary" />
                <MetricCard title="Total Leads" value={totalLeads} subtitle={`${conversionRate}% conversion`} icon={Users} variant="accent" />
                <MetricCard title="Avg Rating" value={avgRating} subtitle={`${positiveReviews} positive reviews`} icon={Star} variant="success" />
                <MetricCard title="Content Created" value={contents.length} subtitle="AI-generated posts" icon={FileText} />
              </div>

              {/* Quick Actions & Top Content */}
              <div className="cards-grid">
                <div className="card">
                  <h3><TrendingUp size={20} /> Quick Actions</h3>

                  <button className="btn primary" onClick={() => navigate(`/content?business=${biz.id}` , {
  state: { userId: user.userId}
})}>
                    <Sparkles size={20} /> Create New Content
                  </button>

                  <button className="btn secondary" onClick={() => navigate(`/leadspage?business=${biz.id}`, {
  state: { userId: user.userId}
})}>
                    <Users size={20} /> View All Leads ({totalLeads})
                  </button>

                  <button className="btn secondary" onClick={() => navigate(`/reviewspage?business=${biz.id}`)}>
                    <Star size={20} /> Manage Reviews ({reviews.length})
                  </button>
                </div>

                <div className="card">
                  <h3>Top Performing Content</h3>

                  {topContent.length > 0 ? topContent.map(content => (
                    <div key={content.id} className="item-row">
                      <div>
                        <p className="item-title">{content.title || 'Untitled'}</p>
                        <p className="item-subtitle">
                          {content.clicks || 0} clicks • {content.leads_generated || 0} leads
                        </p>
                      </div>
                      <span className={`badge ${content.status === 'published' ? 'success' : 'muted'}`}>
                        {content.status || 'draft'}
                      </span>
                    </div>
                  )) : (
                    <p className="text-muted">No content yet</p>
                  )}
                </div>
              </div>

              {/* Recent Leads */}
              <div className="card">
                <h3>Recent Leads</h3>

                {leads.length > 0 ? leads.slice(0, 5).map(lead => (
                  <div key={lead.id} className="item-row">
                    <div>
                      <p className="item-title">{lead.full_name || 'Unnamed Lead'}</p>
                      <p className="item-subtitle">
                        {lead.email || lead.phone || 'No contact info'} 
                      </p>
                      
                    </div>
                    <span className={`badge ${
                      lead.status === 'new' ? 'primary' :
                      lead.status === 'contacted' ? 'warning' :
                      lead.status === 'converted' ? 'success' :
                      'muted'
                    }`}>
                      {lead.status || 'unknown'}
                    </span>
                  </div>
                )) : (
                  <p className="text-muted">No leads yet</p>
                )}
              </div>

            </div>
          );
        })}
      </div>
    </DashboardLayout>
    </div>
  </div>
);

}
export default Dashboard2;
